import { useEffect, useState } from "react";
import { X, Copy, Trash2, Save, Eye, Pencil } from "lucide-react";
import { api } from "./api";
import { toast } from "sonner";
import { STATUSES, PRIORITIES, CATEGORIES, REPLIES, YES_NO } from "./constants";
import { PriorityBadge, ScoreBadge } from "./Badges";
import TrackPackPicker from "./TrackPackPicker";
import ScorePanel from "./ScorePanel";

function Field({ label, children, testId }) {
  return (
    <label className="block" data-testid={testId}>
      <div className="eyebrow mb-1.5">{label}</div>
      {children}
    </label>
  );
}

export default function ContactDrawer({ contact, onClose, onSave, onDelete, tracks = [], scoring }) {
  const [form, setForm] = useState(contact);
  const [preview, setPreview] = useState(null);
  const [parsed, setParsed] = useState(null);
  useEffect(() => {
    const v = (form.adeMeetingDate || "").trim();
    if (!v) { setParsed(null); return; }
    const t = setTimeout(() => api.parseMeetingDate(v).then(setParsed).catch(() => setParsed(null)), 350);
    return () => clearTimeout(t);
  }, [form.adeMeetingDate]);
  useEffect(() => setForm(contact), [contact]);
  if (!contact) return null;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const copy = async () => {
    await navigator.clipboard.writeText(form.initialEmailDraft || "");
    toast.success("Email draft copied");
  };
  const Sel = ({ k, options, testId }) => (
    <select className="select" value={form[k]} onChange={set(k)} data-testid={testId}>
      {options.map((o) => <option key={o}>{o}</option>)}
    </select>
  );

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <aside className="drawer" data-testid="contact-drawer">
        <div className="sticky top-0 z-10 flex items-start gap-3 p-5 border-b" style={{ borderColor: "var(--border)", background: "rgba(17,19,27,0.97)" }}>
          <div className="flex-1">
            <div className="eyebrow">{form.company}</div>
            <h2 className="text-xl font-extrabold mt-1" data-testid="drawer-title">{form.targetName}</h2>
            <div className="flex items-center gap-2 mt-2"><PriorityBadge value={form.priority} /><ScoreBadge value={form.replyScore} testId="drawer-score-badge" /><span className="text-xs" style={{ color: "var(--text-2)" }}>{form.role}</span></div>
          </div>
          <button className="btn btn-sm" onClick={onClose} data-testid="drawer-close-button"><X size={14} /></button>
        </div>

        <div className="p-5 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Target name"><input className="input" value={form.targetName} onChange={set("targetName")} data-testid="drawer-name-input" /></Field>
            <Field label="Company / Label"><input className="input" value={form.company} onChange={set("company")} data-testid="drawer-company-input" /></Field>
            <Field label="Role"><input className="input" value={form.role} onChange={set("role")} /></Field>
            <Field label="Category"><Sel k="category" options={CATEGORIES} testId="drawer-category-select" /></Field>
            <Field label="Priority tier"><Sel k="priority" options={PRIORITIES} testId="drawer-priority-select" /></Field>
            <Field label="Outreach status"><Sel k="outreachStatus" options={STATUSES} testId="drawer-status-select" /></Field>
            <Field label="Email"><input className="input mono" value={form.email} onChange={set("email")} /></Field>
            <Field label="Reply"><Sel k="reply" options={REPLIES} testId="drawer-reply-select" /></Field>
            <Field label="Initial email sent on"><input type="date" className="input mono" value={form.sentDate || ""} onChange={set("sentDate")} data-testid="drawer-sent-date-input" /></Field>
            <Field label="Nudge sent on"><input type="date" className="input mono" value={form.nudgeSentDate || ""} onChange={set("nudgeSentDate")} data-testid="drawer-nudge-date-input" /></Field>
          </div>

          <ScorePanel form={form} setForm={setForm} scoring={scoring} />

          {form.lastReply && (
            <div className="card p-4" style={{ borderColor: "rgba(16,185,129,0.4)" }} data-testid="drawer-last-reply">
              <div className="eyebrow mb-1" style={{ color: "var(--emerald)" }}>Reply detected in Gmail · {form.lastReply.date}</div>
              <div className="text-sm font-semibold">{form.lastReply.subject}</div>
              <div className="text-xs mt-1" style={{ color: "var(--text-2)" }}>{form.lastReply.snippet}</div>
              {form.replyDraftText && (
                <details className="mt-3" data-testid="drawer-ai-reply-draft">
                  <summary className="eyebrow cursor-pointer">AI reply drafted in your Gmail — preview</summary>
                  <div className="pre mt-2">{form.replyDraftText}</div>
                </details>
              )}
            </div>
          )}
          {form.followUpDraftText && (
            <div className="card p-4" style={{ borderColor: "rgba(6,182,212,0.4)" }} data-testid="drawer-followup-draft">
              <details>
                <summary className="eyebrow cursor-pointer" style={{ color: "var(--cyan)" }}>Post-meeting thank-you drafted in Gmail {form.followUpDraftCreatedAt ? `· ${new Date(form.followUpDraftCreatedAt).toLocaleDateString()}` : ""} — preview</summary>
                <div className="pre mt-2">{form.followUpDraftText}</div>
              </details>
            </div>
          )}
          {form.calendarEventLink && (
            <div className="mono text-[11px]" data-testid="drawer-calendar-link">
              <a href={form.calendarEventLink} target="_blank" rel="noreferrer" style={{ color: "var(--cyan)" }}>Google Calendar event ↗</a> · 30-min reminder set
            </div>
          )}
          {form.gmailDraftId && !form.lastReply && (
            <div className="mono text-[11px]" style={{ color: "var(--text-2)" }} data-testid="drawer-gmail-draft-note">Gmail draft created {form.gmailDraftCreatedAt ? new Date(form.gmailDraftCreatedAt).toLocaleDateString() : ""} — review &amp; send it from Gmail.</div>
          )}

          <Field label="Sound fit notes"><textarea className="textarea" style={{ minHeight: 70 }} value={form.soundFitNotes} onChange={set("soundFitNotes")} /></Field>
          <Field label="Public contact route"><textarea className="textarea" style={{ minHeight: 60 }} value={form.publicContactRoute} onChange={set("publicContactRoute")} /></Field>
          <Field label="City / ADE presence notes"><textarea className="textarea" style={{ minHeight: 60 }} value={form.cityNotes} onChange={set("cityNotes")} /></Field>

          <div className="card p-4" style={{ background: "var(--surface-2)" }}>
            <div className="flex items-center justify-between mb-2 gap-2">
              <div className="eyebrow">Initial email draft (personalised)</div>
              <div className="flex gap-2">
                <button className="btn btn-sm" data-testid="toggle-draft-preview-button"
                  onClick={() => (preview ? setPreview(null) : api.contactDraftHtml(form.id).then(setPreview).catch(() => toast.error("Preview failed")))}>
                  {preview ? <><Pencil size={13} /> Edit</> : <><Eye size={13} /> Preview with links</>}
                </button>
                <button className="btn btn-sm btn-amber" onClick={copy} data-testid="copy-email-draft-button"><Copy size={13} /> Copy</button>
              </div>
            </div>
            {preview ? (
              <div className="rounded-xl p-4" style={{ background: "#fff", color: "#202124" }} data-testid="draft-html-preview">
                <div className="text-xs font-semibold mb-3" style={{ color: "#5f6368" }}>Subject: {preview.subject}</div>
                <div dangerouslySetInnerHTML={{ __html: preview.html }} />
              </div>
            ) : (
              <textarea className="textarea" style={{ minHeight: 320 }} value={form.initialEmailDraft} onChange={set("initialEmailDraft")} data-testid="drawer-email-draft-textarea" />
            )}
          </div>

          <div className="card p-4">
            <div className="eyebrow mb-3">ADE meeting</div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Meeting date / time">
                <input className="input mono" placeholder="any format: Thu 3pm · Oct 22 14:00 · 22/10 14h" value={form.adeMeetingDate} onChange={set("adeMeetingDate")} data-testid="contact-meeting-date-input" />
                {form.adeMeetingDate?.trim() && (
                  <div className="mono text-[10px] mt-1" style={{ color: parsed?.ok ? "var(--emerald)" : parsed ? "#f87171" : "var(--text-2)" }} data-testid="meeting-date-parsed">
                    {parsed ? `→ ${parsed.label}` : "…"}
                  </div>
                )}
              </Field>
              <Field label="Location"><input className="input" placeholder="Felix Meritis / showcase venue" value={form.adeMeetingLocation} onChange={set("adeMeetingLocation")} data-testid="contact-meeting-location-input" /></Field>
            </div>
          </div>

          <div className="card p-4">
            <div className="eyebrow mb-3">Post-ADE</div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Follow-up needed after ADE"><Sel k="followUpNeeded" options={YES_NO} testId="drawer-followup-needed-select" /></Field>
              <Field label="Follow-up sent"><Sel k="followUpSent" options={YES_NO} testId="drawer-followup-sent-select" /></Field>
            </div>
          </div>

          <TrackPackPicker form={form} setForm={setForm} tracks={tracks} />

          <Field label="General comments (short, factual)"><textarea className="textarea" style={{ minHeight: 70 }} value={form.comments} onChange={set("comments")} data-testid="drawer-comments-textarea" /></Field>

          <div className="flex items-center gap-2 pt-2 pb-8">
            <button className="btn btn-amber" onClick={() => onSave(form)} data-testid="drawer-save-button"><Save size={14} /> Save changes</button>
            <button className="btn btn-ghost ml-auto" style={{ color: "#FB7185" }} onClick={() => onDelete(form)} data-testid="drawer-delete-button"><Trash2 size={14} /> Remove</button>
          </div>
        </div>
      </aside>
    </>
  );
}
